/**
 * ScaleCalibrationCard (participant)
 *
 * Shown before scoring when the facilitator has opened scale calibration. Each
 * open criterion presents its anchor meanings; the participant confirms "this
 * matches how I'd read it" or flags "I read it differently" with a note. This is
 * the facilitated step that aligns what "5 = High" means across the group.
 */

import { useEffect, useMemo, useState } from 'react';
import { Ruler, Check, X, Loader2 } from 'lucide-react';
import { useScaleCalibrationStore } from '../../../stores/prioritization/scaleCalibrationStore';
import type { ScaleCalibration } from '../../../types/prioritization/scaleCalibration';

function CalibrationItem({
  calibration,
  participantId,
  participantName,
}: {
  calibration: ScaleCalibration;
  participantId: string;
  participantName: string;
}) {
  const acknowledge = useScaleCalibrationStore(s => s.acknowledge);
  const acks = useScaleCalibrationStore(s => s.acks);
  const mine = acks.find(a => a.calibrationId === calibration.id && a.participantId === participantId);

  const [showDiffer, setShowDiffer] = useState(false);
  const [note, setNote] = useState(mine?.note ?? '');
  const [busy, setBusy] = useState(false);

  const submit = async (alignment: 'aligned' | 'differs') => {
    setBusy(true);
    await acknowledge({
      calibrationId: calibration.id,
      participantId,
      participantName,
      alignment,
      note: alignment === 'differs' ? note : undefined,
    });
    setBusy(false);
    setShowDiffer(false);
  };

  return (
    <div className="border border-[var(--rule)] p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-[var(--text-pri)]">
          {calibration.attributeLabel}
          <span className="text-xs font-normal text-[var(--text-ter)]"> · {calibration.scaleLabel}</span>
        </p>
        {mine && (
          <span
            className={`text-xs font-semibold ${mine.alignment === 'aligned' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`}
          >
            {mine.alignment === 'aligned' ? 'Aligned' : 'Flagged'}
          </span>
        )}
      </div>

      <div className="mt-2 space-y-1.5">
        {calibration.anchors.map((a, i) => (
          <div key={`${a.value}-${i}`} className="flex items-start gap-2">
            <span className="mt-0.5 inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded bg-[var(--hover-tint)] text-xs font-semibold text-[var(--text-sec)] flex-shrink-0">
              {a.label}
            </span>
            <span className="text-xs text-[var(--text-sec)]">
              {a.definition?.trim() ? a.definition : <span className="text-[var(--text-ter)] italic">No definition given</span>}
            </span>
          </div>
        ))}
      </div>

      {!showDiffer ? (
        <div className="mt-2.5 flex items-center gap-2">
          <button
            onClick={() => void submit('aligned')}
            disabled={busy}
            className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5  border disabled:opacity-50 ${
              mine?.alignment === 'aligned'
                ? 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
            }`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            This matches how I read it
          </button>
          <button
            onClick={() => setShowDiffer(true)}
            disabled={busy}
            className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5  border disabled:opacity-50 ${
              mine?.alignment === 'differs'
                ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]'
                : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
            }`}
          >
            I read it differently
          </button>
        </div>
      ) : (
        <div className="mt-2.5 space-y-2">
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            autoFocus
            placeholder="How do you read this scale differently? (helps the group align)"
            className="w-full text-sm border border-[var(--rule)] px-2.5 py-1.5 focus:ring-2 focus:ring-amber-300 focus:border-[var(--mustard)] resize-none"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => void submit('differs')}
              disabled={busy}
              className="text-xs font-semibold px-3 py-1.5 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50"
            >
              Submit
            </button>
            <button
              onClick={() => setShowDiffer(false)}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ScaleCalibrationCardProps {
  sessionId: string;
  participantId: string;
  participantName: string;
  onClose: () => void;
}

export function ScaleCalibrationCard({ sessionId, participantId, participantName, onClose }: ScaleCalibrationCardProps) {
  const fetchForSession = useScaleCalibrationStore(s => s.fetchForSession);
  const calibrations = useScaleCalibrationStore(s => s.calibrations);

  useEffect(() => {
    void fetchForSession(sessionId);
  }, [sessionId, fetchForSession]);

  const open = useMemo(
    () => calibrations.filter(c => c.sessionId === sessionId && c.status === 'open'),
    [calibrations, sessionId]
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col bg-[var(--bg-raised)] shadow-xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--slate-dim)] flex items-center justify-center flex-shrink-0">
              <Ruler className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-pri)]">Calibrate the scales</h3>
              <p className="text-xs text-[var(--text-ter)]">
                Confirm you read each scale the same way — or flag where you don&apos;t.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          {open.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)] text-center py-6">
              No scales are open for calibration right now.
            </p>
          ) : (
            open.map(c => (
              <CalibrationItem
                key={c.id}
                calibration={c}
                participantId={participantId}
                participantName={participantName}
              />
            ))
          )}
        </div>

        <div className="px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)] flex justify-end">
          <button
            onClick={onClose}
            className="text-sm font-semibold px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScaleCalibrationCard;
